// src/lib/http-client.ts
import axios, { type AxiosResponse, type AxiosRequestConfig } from "axios";
import { toast } from "sonner";

// Barrel (`@/shared/paket`) yerine yaprak dosyalar: barrel `services/api.ts`
// üzerinden buraya geri döner ve prod build'de döngü/TDZ hatası doğar.
// constants.ts + types.ts hiçbir şey import etmez, güvenli uçlardır.
import { DEMO_KILIT_MESAJI, PAKET_ROTASI } from "@/shared/paket/constants";
import type { HesapKapisiGovdesi } from "@/shared/paket/types";

/**
 * §2 kapısı middleware'de olduğu için hesap askıya alındığında sayfadaki HER query
 * 402 döner. Tek navigasyon yapmak için modül seviyesinde bayrak tutulur.
 */
let hesapKapisiYonlendirmesiYapildi = false;

/**
 * 402 `hesap_askida` / `hesap_hazirlaniyor` bir hata değil, akış kontrolüdür:
 * kullanıcı paket ekranına alınır. `/paket` yalnız muaf uçları (`/paketlerim/`,
 * `/paket-talebi/`) çağırdığı için yönlendirme döngüsü oluşmaz.
 */
function yonlendirPaketEkranina(hata: string) {
  if (typeof window === "undefined") return;
  if (window.location.pathname.startsWith(PAKET_ROTASI)) return;
  if (hesapKapisiYonlendirmesiYapildi) return;

  hesapKapisiYonlendirmesiYapildi = true;
  window.location.href =
    hata === "hesap_hazirlaniyor"
      ? `${PAKET_ROTASI}?durum=hazirlaniyor`
      : PAKET_ROTASI;
}

async function clientLogout() {
  try {
    await fetch("/api/auth/logout", { method: "POST", credentials: "include" });
  } catch {
    // Ignore logout errors to avoid blocking redirects.
  }
}

export const httpClient = axios.create({
  baseURL: "/api/proxy",
  headers: { "Content-Type": "application/json" },
});

function collectApiErrorMessages(responseData: unknown) {
  const errorMessages: string[] = [];

  if (!responseData || typeof responseData !== "object") {
    return errorMessages;
  }

  // DRF ValidationError alan adı olmadan fırlatıldığında gövde düz bir dizi olur
  // (ör. ["Fişte 0 oranlı matrah satırı var ama ... rejimi tanımlı değil."]).
  if (Array.isArray(responseData)) {
    for (const message of responseData) {
      if (typeof message === "string" && message.trim()) {
        errorMessages.push(message);
      }
    }
    return [...new Set(errorMessages)];
  }

  const data = responseData as Record<string, unknown>;

  const genelAlanlar = ["detail", "message", "error"];
  for (const key of genelAlanlar) {
    const value = data[key];
    if (typeof value === "string" && value.trim()) {
      errorMessages.push(value);
    }
  }

  for (const [field, messages] of Object.entries(data)) {
    const fieldName = field
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());

    if (Array.isArray(messages)) {
      const readableMessages = messages.filter(
        (message) => typeof message === "string" || typeof message === "number",
      );

      if (readableMessages.length === 0) {
        continue;
      }

      readableMessages.forEach((message) => {
        errorMessages.push(`${fieldName}: ${message}`);
      });
      continue;
    }

    // Alan hatası dizi yerine düz bir cümle olarak da dönebilir. Sözleşme 2026-08-25'te netleşti:
    // serializer (PATCH) uçları alan hatasını **DRF listesi** olarak döndürür
    // ({"varsayilan_gelir_hesap": ["770 … borç karakterli bir hesap; gelir varsayılanı olamaz."]});
    // düz string yalnız aynı davranışı string olarak veren uçlara karşı tolerans olarak okunur —
    // eskiden orada hiçbir mesaj toplanamıyor, kullanıcı içeriksiz "Lütfen girdiğiniz bilgileri
    // kontrol edin" görüyordu.
    //
    // Yalnız cümleye benzeyen (boşluk içeren) değerler basılır: `{"hata": "yon_uyumsuz"}` gibi
    // makine kodları kullanıcı metni değildir ve kendi ekranında zaten çevriliyor.
    if (
      typeof messages === "string"
      && !genelAlanlar.includes(field)
      && /\s/.test(messages.trim())
    ) {
      errorMessages.push(`${fieldName}: ${messages}`);
    }
  }

  return [...new Set(errorMessages)];
}

// Response interceptor for error handling
httpClient.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error) => {
    const status = error.response?.status;

    if (error.response) {

      switch (status) {
        case 400:
        case 422: {
          const errorMessages = collectApiErrorMessages(error.response.data);

          if (errorMessages.length > 0) {
            // Tek toast içinde tüm hataları göster
            toast.error(errorMessages.join('\n'), {
              style: { whiteSpace: 'pre-line' }, // \n'leri satırbaşı olarak yorumla
            });
          } else {
            toast.error('Lütfen girdiğiniz bilgileri kontrol edin.');
          }
          break;
        }
        // 500'de gövde çoğu kez okunabilir bir alan taşımaz (Django'nun HTML hata
        // sayfası); o durumda "bilgilerinizi kontrol edin" demek kullanıcıyı kendi
        // girdisini/dosyasını aramaya gönderiyordu — hata sunucuda.
        case 500: {
          const errorMessages = collectApiErrorMessages(error.response.data);

          if (errorMessages.length > 0) {
            toast.error(errorMessages.join('\n'), {
              style: { whiteSpace: 'pre-line' },
            });
          } else {
            toast.error('Sunucuda beklenmeyen bir hata oluştu. Lütfen tekrar deneyin.');
          }
          break;
        }
        case 409: {
          const message =
            collectApiErrorMessages(error.response.data)[0] ||
            "Kayıt durumu bu işlem için uygun değil.";
          toast.error(message);
          break;
        }
        // Proxy kendi ağ hatasında da 502 üretir ({code:"UPSTREAM_ERROR"}); onun
        // İngilizce "Bad gateway" mesajı backend'in gerçek 502'sini gölgelemesin.
        case 502: {
          const isProxyFailure =
            (error.response.data as { code?: string } | undefined)?.code ===
            "UPSTREAM_ERROR";
          const message = isProxyFailure
            ? "Sunucuya ulaşılamadı. Lütfen tekrar deneyin."
            : collectApiErrorMessages(error.response.data)[0] ||
              "GİB ile geçici bir bağlantı sorunu oluştu. Lütfen tekrar deneyin.";
          toast.error(message);
          break;
        }
        case 401:
          toast.error('Lütfen giriş yapın.');
          // Örneğin, token süresi dolmuşsa yönlendirme yapılabilir
          // Redirect to login if we get logged out
          await clientLogout()
          // window.location.href = "/login";
          window.location.href = '/login';
          break;
        // §2/§3 — gövde DÜZ JSON'dur (DRF `detail` biçimi DEĞİL); `hata` alanı
        // hangi kapının kapandığını söyler.
        case 402: {
          const data = error.response.data as HesapKapisiGovdesi | undefined;

          if (data?.hata === 'hesap_askida' || data?.hata === 'hesap_hazirlaniyor') {
            yonlendirPaketEkranina(data.hata);
            break;
          }
          if (data?.hata === 'kota_dolu') {
            // Backend'in mesajı paket adını ve limiti taşır; ilgili form ayrıca
            // kendi üstünde KotaDoluUyarisi basar.
            toast.error(data.mesaj ?? 'Paket sınırınıza ulaştınız.');
            break;
          }
          toast.error(data?.mesaj ?? 'İşlem tamamlanamadı.');
          break;
        }
        case 403: {
          // §4 — demo gönderim kilidi kayıt varlığından ÖNCE çalışır; bu 403'ü
          // "kayıt bulunamadı" ya da "yetkin yok" sanma.
          const data = error.response.data as { hata?: string; mesaj?: string } | undefined;
          if (data?.hata === 'demo_gonderim_kilitli') {
            toast.error(data.mesaj ?? DEMO_KILIT_MESAJI);
            break;
          }
          toast.error('Bu işlem için yetkiniz yok.');
          break;
        }
        case 404:
          toast.error('Kayıt bulunamadı.');
          break;
        // case 500:
        //   toast.error('Internal Server Error');
        //   break;
        default:
          toast.error(`Unexpected error: ${status}`);
      }
    } else if (error.request) {
      // İstek yapıldı ama yanıt alınamadı (network hatası vs.)
      toast.error('Network Error:', error.request);
    } else {
      // İstek yapılmadan önce bir hata oluştu
      toast.error('Error:', error.message);
    }

    return Promise.reject(error);
  }
);

// Helper functions for common HTTP methods
export const clientApi = {
  get: <T = never>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    httpClient.get(url, config),

  post: <T = never>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    httpClient.post(url, data, config),

  put: <T = never>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    httpClient.put(url, data, config),

  patch: <T = never>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    httpClient.patch(url, data, config),

  delete: <T = never>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> =>
    httpClient.delete(url, config),
};
